import React, { useState, useEffect } from 'react';
import { Alert, Dialog, DialogContent, DialogTitle, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MFASetup from './MFASetup';
import ApiService from '../services/api';

interface MFAGuardProps {
  children: React.ReactNode;
  requireMFA?: boolean;
  userEmail?: string;
}

const MFAGuard: React.FC<MFAGuardProps> = ({ children, requireMFA = false, userEmail }) => {
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (requireMFA) {
      checkMFARequirement();
    }
  }, [requireMFA]);

  const checkMFARequirement = async () => {
    try {
      setChecking(true);
      
      // Try to access any authenticated endpoint to check if MFA is required
      // Using accounts endpoint as it's less privileged than admin endpoints
      await ApiService.getAccounts();
      
      // If we get here, MFA is not required or already set up
      setMfaRequired(false);
      
    } catch (error: any) {
      console.log('MFA Check Error:', error);
      console.log('Error response:', error.response?.data);
      
      // Check for MFA_REQUIRED error type or message content
      const errorData = error.response?.data;
      const isMFARequired = errorData?.error_type === 'MFA_REQUIRED' || 
                           error.message.includes('MFA_REQUIRED') ||
                           errorData?.message?.includes('Multi-factor authentication is required');
      
      if (isMFARequired) {
        console.log('MFA Required - opening setup dialog');
        setMfaRequired(true);
        setMfaSetupOpen(true);
      } else {
        console.error('Error checking MFA requirement:', error);
        // For other errors, don't block access
        setMfaRequired(false);
      }
    } finally {
      setChecking(false);
    }
  };

  const handleMFASetupComplete = () => {
    setMfaRequired(false);
    setMfaSetupOpen(false);
    // Optionally refresh the page or reload user data
    window.location.reload();
  };

  const handleMFASetupCancel = () => {
    setMfaSetupOpen(false);
    // Redirect to dashboard since admin access is not available
    navigate('/dashboard');
  };

  if (checking) {
    return (
      <Dialog open={true}>
        <DialogTitle>Checking Authentication</DialogTitle>
        <DialogContent>
          <Typography>Verifying your access permissions...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (mfaRequired) {
    return (
      <>
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Multi-factor authentication is required to access the application. 
            Please complete the MFA setup to continue.
          </Alert>
          {children}
        </Box>
        <MFASetup
          open={mfaSetupOpen}
          onClose={handleMFASetupCancel}
          onComplete={handleMFASetupComplete}
          userEmail={userEmail}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default MFAGuard;